import type { DateType } from '@openx/types/date.ts';
import { differenceInDays, format, isValid, parse, startOfMinute } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import flow from 'lodash/flow';

export const apiDateFormat = 'yyyy-MM-dd HH:mm:ss';
export const dateTimePickerInputFormat = 'yyyy-MM-dd hh:mm a';

export const fromUTCDate = (inputDate: string) => {
  return parse(inputDate, 'yyyy-MM-dd', new Date());
};

export const fromUTCDateTime = (inputDate: string) => {
  return parse(inputDate, apiDateFormat, new Date());
};

export function isSoonToDate(date: DateType, soonWindow = 10): boolean {
  if (!date) {
    return false;
  }

  const parsedDate = new Date(date);

  if (!isValid(parsedDate)) {
    return false;
  }

  const daysRemaining = differenceInDays(parsedDate, new Date());

  return daysRemaining <= soonWindow;
}

export const toUTCDate = (inputDate: DateType): string | null => {
  return toUTCString(inputDate, 'yyyy-MM-dd');
};

export const toUTCDateTime = (inputDate: DateType): string | null => {
  return toUTCString(inputDate);
};

export const ensureDate = (inputDate: DateType) => {
  if (!inputDate) {
    return null;
  }

  const parsedDate = new Date(inputDate);

  if (!isValid(parsedDate)) {
    return null;
  }

  return parsedDate;
};

export const ensureDateStartOfMinute: typeof ensureDate = flow([
  ensureDate,
  (date: ReturnType<typeof ensureDate>) => date && startOfMinute(date),
]);

export const toUTCString = (inputDate: DateType, pattern = apiDateFormat): string | null => {
  const utcDate = ensureDateStartOfMinute(inputDate);
  return utcDate && format(utcDate, pattern);
};

/**
 * Formats a given `Date` object into a string suitable for a date time picker input field.
 * The output format is `yyyy-MM-dd hh:mm a`, e.g. "2023-03-15 02:30 PM".
 *
 * @param date - The `Date` object to be formatted.
 * @returns A formatted date string.
 *
 */
export const formatDateToDateTimePickerInputFormat = (date: Date) =>
  formatInTimeZone(date, 'UTC', dateTimePickerInputFormat);

/**
 * Formats a given `Date` object into a string in the API's expected format.
 * The output format is 'yyyy-MM-dd HH:mm:ss', e.g. "2023-03-15 14:30:00".
 *
 * @param date - The `Date` object to be formatted.
 * @returns A formatted date string.
 */
export const formatDateToApiFormat = (date: Date) => formatInTimeZone(date, 'UTC', apiDateFormat);
